import React, { useState, useEffect } from 'react';
import {
    Container, Typography, Box, TextField, Button, Grid, Paper, Autocomplete, Snackbar, Alert
} from '@mui/material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

export default function SellCoin() {
    const navigate = useNavigate();
    const [coins, setCoins] = useState([]);
    const [selectedCoin, setSelectedCoin] = useState(null);
    const [saleData, setSaleData] = useState({
        salePrice: '',
        saleDate: new Date().toISOString().split('T')[0],
        saleLocation: ''
    });

    // Notification State
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
    const showNotification = (message, severity = 'success') => {
        setNotification({ open: true, message, severity });
    };
    const handleCloseNotification = (event, reason) => {
        if (reason === 'clickaway') return;
        setNotification({ ...notification, open: false });
    };

    useEffect(() => {
        // Fetch available coins to populate the dropdown
        axios.get(`${process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001')}/coins`)
            .then(res => setCoins(res.data))
            .catch(err => console.error("Error loading coins:", err));
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setSaleData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedCoin) {
            showNotification("Please select a coin to sell.", 'warning');
            return;
        }

        const soldCoinPayload = {
            ...selectedCoin,
            sold_details: {
                sale_price: parseFloat(saleData.salePrice) || 0,
                sale_date: saleData.saleDate,
                sale_location: saleData.saleLocation
            },
            original_id: selectedCoin.id || selectedCoin.pcgs_id // Keep a reference
        };

        try {
            // 1. Add to sold_coins
            await axios.post(`${process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001')}/sold_coins`, soldCoinPayload);

            const deleteId = selectedCoin.id || selectedCoin.pcgs_id;
            await axios.delete(`${process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001')}/coins/${deleteId}`);

            showNotification('Coin sold successfully!', 'success');
            setTimeout(() => navigate('/'), 1500);
        } catch (err) {
            console.error('Error processing sale:', err);
            showNotification('Failed to process sale. ' + (err.response?.data ? JSON.stringify(err.response.data) : err.message), 'error');
        }
    };

    return (
        <Container maxWidth="md">
            <Paper sx={{ p: 4, mt: 4 }}>
                <Typography variant="h5" gutterBottom>Sell Coin</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Select a coin from your inventory to mark it as sold.
                </Typography>

                <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Autocomplete
                                options={coins}
                                getOptionLabel={(option) => `${option.date} ${option.denomination} ${option.series} (PCGS: ${option.pcgs_id})`}
                                value={selectedCoin}
                                onChange={(event, newValue) => setSelectedCoin(newValue)}
                                renderInput={(params) => <TextField {...params} label="Select Coin to Sell" fullWidth />}
                            />
                        </Grid>

                        {selectedCoin && (
                            <>
                                <Grid item xs={12}>
                                    <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                                        <Typography variant="subtitle2">Selected Coin Details:</Typography>
                                        <Typography variant="body1">{selectedCoin.date} {selectedCoin.denomination} - {selectedCoin.major}</Typography>
                                        {selectedCoin.specimens && selectedCoin.specimens[0] && (
                                            <Typography variant="body2" color="text.secondary">
                                                Paid: ${selectedCoin.specimens[0].purchase_price}
                                            </Typography>
                                        )}
                                    </Box>
                                </Grid>
                            </>
                        )}

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Sale Price"
                                name="salePrice"
                                type="number"
                                value={saleData.salePrice}
                                onChange={handleChange}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Sale Date"
                                name="saleDate"
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                value={saleData.saleDate}
                                onChange={handleChange}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Sale Location / Buyer"
                                name="saleLocation"
                                value={saleData.saleLocation}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid item xs={12} sx={{ mt: 2 }}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="secondary"
                                size="large"
                                disabled={!selectedCoin}
                            >
                                Confirm Sale
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Paper>
            <Snackbar open={notification.open} autoHideDuration={6000} onClose={handleCloseNotification}>
                <Alert onClose={handleCloseNotification} severity={notification.severity} sx={{ width: '100%' }}>
                    {notification.message}
                </Alert>
            </Snackbar>
        </Container>
    );
}
